
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Hash, Star, BarChart3, Layers } from 'lucide-react';

interface TagDistributionProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

const TagDistribution: React.FC<TagDistributionProps> = ({ timeframe }) => {
  // Mock data for tag analytics
  const mockTags = [
    { id: 1, name: "Technology", count: 28, growth: 12, engagement: 85 },
    { id: 2, name: "Politics", count: 23, growth: -5, engagement: 76 },
    { id: 3, name: "Health", count: 19, growth: 8, engagement: 92 },
    { id: 4, name: "Environment", count: 17, growth: 15, engagement: 88 },
    { id: 5, name: "Business", count: 16, growth: 4, engagement: 72 },
    { id: 6, name: "Science", count: 14, growth: 7, engagement: 81 },
    { id: 7, name: "Culture", count: 12, growth: 2, engagement: 79 },
    { id: 8, name: "Sports", count: 10, growth: -2, engagement: 83 },
    { id: 9, name: "Education", count: 8, growth: 6, engagement: 77 },
    { id: 10, name: "Entertainment", count: 7, growth: 1, engagement: 86 },
  ];

  // Mock data for trending tags
  const mockTrendingTags = [
    { id: 1, name: "AI Ethics", growth: 48 },
    { id: 2, name: "Climate Action", growth: 32 },
    { id: 3, name: "Future of Work", growth: 27 },
    { id: 4, name: "Mental Health", growth: 23 },
    { id: 5, name: "Data Privacy", growth: 19 },
  ];

  // Mock recommendations based on tag performance
  const mockRecommendations = [
    "Your 'Technology' and 'Environment' tags show the highest growth. Consider focusing more content in these areas.",
    "The 'Health' tag has the highest engagement rate. Your readers respond well to health-related content.",
    "Tags like 'Politics' and 'Sports' have seen declining interest. Consider refreshing your approach to these topics.",
    "Content with multiple tags tends to perform 27% better than single-tag content.",
    "Your readers engage more with highly specific tags rather than broad categories."
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Tag Performance Analysis */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Hash className="mr-2 h-5 w-5" />
              Tag Performance
            </CardTitle>
            <CardDescription>
              Distribution of your content across different tags
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockTags.slice(0, 6).map(tag => (
                <div key={tag.id} className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="font-medium text-sm">{tag.name}</span>
                      <Badge variant="outline" className="ml-2 text-xs">
                        {tag.count} articles
                      </Badge>
                    </div>
                    <div className="mt-1 w-full bg-secondary h-2 rounded-full">
                      <div 
                        className="bg-primary h-2 rounded-full"
                        style={{ width: `${(tag.count / mockTags[0].count) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="ml-4 flex items-center">
                    <span className={`text-sm ${tag.growth >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                      {tag.growth > 0 ? '+' : ''}{tag.growth}%
                    </span>
                  </div>
                </div>
              ))}
              
              <Button variant="outline" size="sm" className="w-full mt-2">
                View all tags
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Trending Tags */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="mr-2 h-5 w-5" />
              Trending Tags
            </CardTitle>
            <CardDescription>
              Tags with the highest growth in the {timeframe} period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mockTrendingTags.map(tag => (
                <div key={tag.id} className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <div className="bg-primary/10 text-primary p-2 rounded-full mr-3">
                      <Hash className="h-5 w-5" />
                    </div>
                    <span className="font-medium">{tag.name}</span>
                  </div>
                  <Badge variant="default">+{tag.growth}%</Badge>
                </div>
              ))}
              
              <Button variant="secondary" size="sm" className="w-full mt-2">
                Explore trending topics
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tag Engagement Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <BarChart3 className="mr-2 h-5 w-5" />
            Tag Engagement Analysis
          </CardTitle>
          <CardDescription>
            How your audience engages with different topic tags
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-4 col-span-2">
              {mockTags.slice(0, 5).map(tag => (
                <div key={tag.id} className="flex items-center gap-4">
                  <div className="w-24 text-sm font-medium">{tag.name}</div>
                  <div className="flex-1 bg-secondary h-4 rounded-full">
                    <div 
                      className="bg-directpress-orange h-4 rounded-full flex items-center justify-end pr-2"
                      style={{ width: `${tag.engagement}%` }}
                    >
                      <span className="text-xs text-white font-medium">{tag.engagement}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-muted p-4 rounded-lg">
              <h4 className="font-semibold mb-3 flex items-center">
                <Star className="h-4 w-4 mr-1 text-yellow-500" />
                Highest Engagement
              </h4>
              <div className="space-y-3">
                {mockTags.sort((a, b) => b.engagement - a.engagement).slice(0, 3).map(tag => (
                  <div key={tag.id} className="flex justify-between items-center">
                    <Badge variant="outline">{tag.name}</Badge>
                    <span className="text-sm font-medium">{tag.engagement}% engagement</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tag Strategy Recommendations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Layers className="mr-2 h-5 w-5" />
            Tag Strategy Recommendations
          </CardTitle>
          <CardDescription>
            Insights to optimize your content tagging strategy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {mockRecommendations.map((rec, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-directpress-blue text-white flex items-center justify-center text-sm font-medium">
                  {index + 1}
                </div>
                <span className="text-sm">{rec}</span>
              </li>
            ))}
          </ul>
          
          <div className="mt-6 bg-muted p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Topic Content Gap Analysis</h4>
            <p className="text-sm text-muted-foreground mb-3">
              Based on your audience interests, consider creating content in these underserved tags:
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">Cybersecurity</Badge>
              <Badge variant="outline">Sustainable Living</Badge>
              <Badge variant="outline">Personal Finance</Badge>
              <Badge variant="outline">AI in Healthcare</Badge>
              <Badge variant="outline">Global Supply Chain</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TagDistribution;
